import { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react'
import { DropdownMenu } from '@pinchenterprisesnpm/friday-ui'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { ISplitButtonGroupProps } from './split-button-group.types'
import {
  HiddenMeasureContainer,
  SplitButtonGroupContainer,
  SplitButton,
  SplitButtonIcon,
  dropDownMenuStyles,
} from './split-button-group.styles'
import { getVisibleCount } from './split-button-group.utils'
import { CustomMenuItem } from './components'

const { DropdownMenuList, DropdownMenuButton } = DropdownMenu

export function SplitButtonGroup<T>({
  items,
  lines = 1,
  onSelect,
  sortOnSelect = false,
  maxLabelLength,
  isDisabled,
  ...containerProps
}: ISplitButtonGroupProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)

  // stores the number of items that can be displayed before we need the "More" button
  const [visibleCount, setVisibleCount] = useState(items.length)
  const [sort, setSort] = useState(false)

  // refs used solely for measurement – they live inside the hidden measurement container
  const measureButtonRefs = useRef<(HTMLButtonElement | null)[]>([])
  const measureMoreRef = useRef<HTMLButtonElement>(null)

  // Track if we're currently recalculating to prevent loops
  const isRecalculating = useRef(false)

  const recalculate = () => {
    // Prevent concurrent recalculations
    if (isRecalculating.current) return

    try {
      isRecalculating.current = true

      const containerWidth = (containerRef.current?.clientWidth ?? 0) - 5
      if (!containerWidth || containerWidth <= 0) {
        isRecalculating.current = false
        return
      }

      // Ensure we have valid button refs
      const validRefs = measureButtonRefs.current.filter(ref => ref !== null)
      if (validRefs.length === 0) {
        isRecalculating.current = false
        return
      }

      const widths = measureButtonRefs.current.map(btn => btn?.offsetWidth ?? 0)
      const moreWidth = measureMoreRef.current?.offsetWidth ?? 0

      let gap = 0
      if (containerRef.current) {
        const computedStyles = window.getComputedStyle(containerRef.current)
        const gapValue = computedStyles.columnGap || computedStyles.gap || '0'
        const parsed = parseFloat(gapValue as string)
        gap = isNaN(parsed) ? 0 : parsed
      }

      const selectedIndex = items.findIndex(i => i.isSelected)
      const { count, sort } = getVisibleCount(
        widths,
        lines,
        gap,
        moreWidth,
        containerWidth,
        selectedIndex,
      )

      // Only update if the count actually changed to prevent unnecessary re-renders
      setVisibleCount(prevCount => {
        if (prevCount !== count) {
          return count
        }
        return prevCount
      })
      setSort(prevSort => {
        if (prevSort !== sort) {
          return sort
        }
        return prevSort
      })
    } catch {
      // Do nothing
    } finally {
      isRecalculating.current = false
    }
  }

  // If sorting is enabled, bring the selected item to front for layout purposes
  const itemsForLayout = useMemo(() => {
    if (!sortOnSelect) return items

    const selectedIndex = items.findIndex(i => i.isSelected)
    // If no item selected or it is already within visible range, keep order
    if (selectedIndex <= 0 || !sort) return items

    // Bring selected item to front
    return [
      items[selectedIndex],
      ...items.slice(0, selectedIndex),
      ...items.slice(selectedIndex + 1),
    ]
  }, [items, sortOnSelect, sort])

  const visibleItems = itemsForLayout.slice(0, visibleCount)
  const hiddenItems = itemsForLayout.slice(visibleCount)

  // Helper to truncate the label if maxLabelLength is provided
  const getDisplayLabel = (label: string) => {
    if (maxLabelLength !== undefined && label.length > maxLabelLength) {
      return label.slice(0, maxLabelLength) + '...'
    }
    return label
  }

  // (Re)calculate whenever the amount of space changes or the inputs change
  useLayoutEffect(() => {
    recalculate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsForLayout, lines, maxLabelLength])

  // Attach ResizeObserver to container so that we recalculate on size changes
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver(() => {
      recalculate()
    })
    observer.observe(container)

    // Fallback for window resize (older browsers)
    window.addEventListener('resize', recalculate)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', recalculate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, lines, maxLabelLength])

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <SplitButtonGroupContainer ref={containerRef} {...containerProps}>
        {visibleItems.map(item => {
          return (
            <SplitButton
              type="button"
              key={item.id}
              onClick={event => onSelect(item, event)}
              isSelected={item.isSelected}
              disabled={isDisabled}
            >
              {getDisplayLabel(item.label)}
            </SplitButton>
          )
        })}

        {hiddenItems.length > 0 && (
          <DropdownMenu placement="bottom-start">
            {({ isOpen }) => (
              <>
                <DropdownMenuButton
                  renderTriggerElement={(toggleMenu, ref) => (
                    <SplitButton
                      type="button"
                      onClick={toggleMenu}
                      ref={ref}
                      disabled={isDisabled}
                    >
                      More
                      <SplitButtonIcon
                        icon={isOpen ? faChevronUp : faChevronDown}
                      />
                    </SplitButton>
                  )}
                />
                <DropdownMenuList style={dropDownMenuStyles}>
                  {hiddenItems.map(item => (
                    <CustomMenuItem
                      key={item.id}
                      isDisabled={isDisabled}
                      onClick={event => onSelect(item, event)}
                    >
                      {getDisplayLabel(item.label)}
                    </CustomMenuItem>
                  ))}
                </DropdownMenuList>
              </>
            )}
          </DropdownMenu>
        )}
      </SplitButtonGroupContainer>

      {/* Hidden container used for measuring the intrinsic widths of all buttons */}
      <HiddenMeasureContainer>
        {items.map((item, index) => (
          <SplitButton
            type="button"
            key={item.id}
            isSelected={item.isSelected}
            disabled={isDisabled}
            ref={el => {
              measureButtonRefs.current[index] = el
            }}
          >
            {getDisplayLabel(item.label)}
          </SplitButton>
        ))}
        {/* The "More" button that lives only inside the measurement container */}
        <SplitButton type="button" ref={measureMoreRef} disabled={isDisabled}>
          More <SplitButtonIcon icon={faChevronDown} />
        </SplitButton>
      </HiddenMeasureContainer>
    </>
  )
}
